import { ArrowUpRight, ExternalLink } from "lucide-react";

const IMG = "/assets/fin-reco-demo.png";

const Chrome = () => (
  <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#F1EDE6] border-b border-[#3E2818]/8">
    <span className="w-2.5 h-2.5 rounded-full bg-[#E88B6B]" />
    <span className="w-2.5 h-2.5 rounded-full bg-[#E8C15E]" />
    <span className="w-2.5 h-2.5 rounded-full bg-[#9CB88A]" />
    <span className="ml-2 flex-1 truncate rounded-md bg-white/70 px-2.5 py-1 font-mono2 text-[10px] text-[#3E2818]/55">fin-reco.demo.intugle.ai</span>
  </div>
);
const Shot = ({ ratio = "aspect-[16/11]", children }) => (
  <div className={`relative ${ratio} overflow-hidden`}>
    <img src={IMG} alt="demo" className="absolute inset-0 w-full h-full object-cover object-top" />
    {children}
  </div>
);
const Card = ({ children }) => (
  <div className="relative rounded-xl overflow-hidden border border-[#3E2818]/12 shadow-[0_18px_48px_-30px_rgba(62,40,24,0.5)]">{children}</div>
);

/* G · Clean white footer card */
const G = () => (
  <Card>
    <Chrome />
    <Shot ratio="aspect-[16/10]" />
    <div className="flex items-center justify-between bg-white px-4 py-3.5 border-t border-[#3E2818]/8">
      <div>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730]">Finance Close</p>
        <p className="text-[14px] font-semibold text-[#3E2818] mt-0.5">Explore the live demo</p>
      </div>
      <span className="w-8 h-8 rounded-full bg-[#211307] text-white flex items-center justify-center shrink-0"><ArrowUpRight size={16} strokeWidth={2.4} /></span>
    </div>
  </Card>
);

/* H · Compact dark pill, bottom-left */
const H = () => (
  <Card>
    <Chrome />
    <Shot>
      <span className="absolute left-3 bottom-3 flex items-center gap-2 rounded-full bg-[#211307]/90 backdrop-blur text-white pl-3.5 pr-3 py-2 text-[12.5px] font-semibold">
        Explore the live demo <ArrowUpRight size={14} strokeWidth={2.4} className="text-[#E8AA68]" />
      </span>
    </Shot>
  </Card>
);

/* I · Inverse light gradient (fades to cream) */
const I = () => (
  <Card>
    <Chrome />
    <Shot>
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#F7F5F0] via-[#F7F5F0]/70 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4 flex items-end justify-between">
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#8A5730]">Finance Close</p>
          <p className="text-[15px] font-semibold text-[#3E2818] mt-0.5">Explore the live demo</p>
        </div>
        <span className="w-9 h-9 rounded-full bg-[#211307] text-white flex items-center justify-center shrink-0"><ArrowUpRight size={18} strokeWidth={2.4} /></span>
      </div>
    </Shot>
  </Card>
);

/* J · White footer with gold top hairline */
const J = () => (
  <Card>
    <Chrome />
    <Shot ratio="aspect-[16/10]" />
    <div className="border-t-2 border-[#EDA13A] bg-white px-4 py-3.5 flex items-center justify-between">
      <div>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#B87A32]">Finance Close</p>
        <p className="text-[14px] font-semibold text-[#3E2818] mt-0.5">Explore the live demo</p>
      </div>
      <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#B87A32]">Open <ArrowUpRight size={15} strokeWidth={2.4} /></span>
    </div>
  </Card>
);

/* K · Floating gold button only, no band */
const K = () => (
  <Card>
    <Chrome />
    <Shot>
      <span className="absolute right-3 bottom-3 flex items-center gap-2 rounded-full bg-[#EDA13A] text-[#211307] px-4 py-2.5 text-[13px] font-semibold shadow-[0_10px_26px_-8px_rgba(237,161,58,0.7)]">
        Open live demo <ExternalLink size={14} strokeWidth={2.4} />
      </span>
    </Shot>
  </Card>
);

/* L · Two-line white footer with button */
const L = () => (
  <Card>
    <Chrome />
    <Shot ratio="aspect-[16/9]" />
    <div className="bg-white px-4 py-4">
      <p className="text-[14px] font-semibold text-[#3E2818]">See the reconciliation agent live</p>
      <p className="text-[12px] text-[#3E2818]/55 mt-0.5 leading-snug">Interactive Finance Close demo — no signup.</p>
      <span className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#211307] text-[#F7F5F0] px-3.5 py-2 text-[12.5px] font-semibold">
        Open live demo <ExternalLink size={13} strokeWidth={2.2} />
      </span>
    </div>
  </Card>
);

const DarkContext = () => (
  <div className="rounded-xl bg-[#211307] text-[#F7F5F0] p-5 lg:p-6">
    <h4 className="font-display text-xl font-medium">Hire-to-Retire</h4>
    <p className="text-[13px] text-white/70 mt-2 leading-relaxed max-w-2xl">Transform the complete employee lifecycle with AI agents working across every stage of the employee journey. Attract &amp; Hire → Onboard → Serve → Develop &amp; Retain → Offboard.</p>
  </div>
);

const OPTIONS = [
  { id: "g", title: "Option G · Clean White Footer", note: "Screenshot sits clean; a white footer strip below (outside the image) carries the label + arrow. Matches the white agent cards perfectly.", comp: G },
  { id: "h", title: "Option H · Compact Dark Pill", note: "A small dark pill floats in the bottom-left; the rest of the screenshot stays visible. Much lighter dark presence than a full band.", comp: H },
  { id: "i", title: "Option I · Inverse Light Gradient", note: "The gradient fades to cream/ivory (not dark) with dark text — the opposite tone to the dark block, so zero clash.", comp: I },
  { id: "j", title: "Option J · Gold-Hairline Footer", note: "White footer with a crisp gold top line. Light, editorial, and adds a touch of the brand amber.", comp: J },
  { id: "k", title: "Option K · Floating Gold Button", note: "No caption band at all — just a single gold 'Open live demo' button pinned bottom-right. Screenshot stays fully visible.", comp: K },
  { id: "l", title: "Option L · White Footer + Button", note: "A richer white footer with a two-line description and an explicit button. Most informative and self-explanatory.", comp: L },
];

export default function DemoThumbBottomOptions2() {
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">Demo Thumbnail Bottom · More Options</p>
        <h1 className="font-display text-2xl lg:text-3xl font-medium mt-2 tracking-tight text-[#3E2818]">More bottom treatments for the thumbnail</h1>
        <p className="text-sm text-[#3E2818]/60 mt-3 max-w-2xl leading-relaxed">Dark Hire-to-Retire block shown left for context. Each thumbnail tries a different bottom treatment. Tell me the letter.</p>

        <div className="mt-10 space-y-14">
          {OPTIONS.map((o) => {
            const Cm = o.comp;
            return (
              <div key={o.id}>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#E8AA68] mb-3">{o.title}</p>
                <div className="grid lg:grid-cols-[1fr_0.55fr] gap-6 lg:gap-10 items-start">
                  <DarkContext />
                  <Cm />
                </div>
                <p className="text-[12px] text-[#3E2818]/55 mt-3 leading-snug max-w-3xl">{o.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
